import { useState, type ComponentProps } from 'react';
import { cn } from '@/lib/cn';
import { getActiveCurrency, parseMoneyInput } from '@/lib/money';
import {
  controlBase,
  controlBorder,
  controlSizes,
  useFieldContext,
  type ControlSize,
} from './field-context';

interface BaseProps extends Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'size'> {
  /** Controlled value; `null` = empty. */
  value: number | null;
  onValueChange: (value: number | null) => void;
  size?: ControlSize;
  invalid?: boolean;
  min?: number;
  max?: number;
}

function useNumericField(
  value: number | null,
  format: (n: number) => string,
  parse: (raw: string) => number | null,
  onValueChange: (v: number | null) => void,
  clamp: (n: number) => number,
) {
  const [text, setText] = useState(() => (value === null ? '' : format(value)));
  const [focused, setFocused] = useState(false);
  const [prev, setPrev] = useState(value);

  // Sync from outside (form reset, server value) while the user is not typing.
  if (prev !== value) {
    setPrev(value);
    if (!focused) setText(value === null ? '' : format(value));
  }

  return {
    text,
    onChange(raw: string) {
      setText(raw);
      const n = parse(raw);
      onValueChange(n === null ? null : clamp(n));
    },
    onFocus() {
      setFocused(true);
    },
    onBlur() {
      setFocused(false);
      const n = parse(text);
      setText(n === null ? '' : format(clamp(n)));
    },
  };
}

export interface NumberInputProps extends BaseProps {
  /** Allowed decimals. 0 = integers only. Default 0. */
  decimals?: number;
}

/**
 * Numeric text input (type=text + inputMode so mobile shows the numpad and there is no scroll-wheel
 * accident). ArrowUp/ArrowDown step by `step`. Emits a number or null.
 */
export function NumberInput({
  value,
  onValueChange,
  decimals = 0,
  min,
  max,
  step = 1,
  size = 'md',
  invalid,
  className,
  id,
  onBlur,
  onFocus,
  onKeyDown,
  ...rest
}: NumberInputProps & { step?: number }) {
  const field = useFieldContext();
  const isInvalid = invalid ?? field?.invalid ?? false;
  const clamp = (n: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
  const pattern = decimals > 0 ? new RegExp(`^-?\\d*(\\.\\d{0,${decimals}})?$`) : /^-?\d*$/;
  const parse = (raw: string) => {
    const s = raw.trim();
    if (s === '' || s === '-' || s === '.' || !pattern.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  const f = useNumericField(value, (n) => String(n), parse, onValueChange, clamp);
  return (
    <input
      id={id ?? field?.id}
      type="text"
      inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      autoComplete="off"
      value={f.text}
      aria-invalid={isInvalid || undefined}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      onChange={(e) => {
        if (
          e.target.value === '' ||
          e.target.value === '-' ||
          pattern.test(e.target.value) ||
          e.target.value === '.'
        ) {
          f.onChange(e.target.value);
        }
      }}
      {...rest}
      onFocus={(e) => {
        f.onFocus();
        e.target.select();
        onFocus?.(e);
      }}
      onBlur={(e) => {
        f.onBlur();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          const delta = e.key === 'ArrowUp' ? step : -step;
          const next = clamp(Math.round(((value ?? 0) + delta) * 1000) / 1000);
          onValueChange(next);
          f.onChange(String(next));
        }
      }}
      className={cn(
        controlBase,
        controlSizes[size],
        controlBorder(isInvalid),
        'tabular text-right',
        className,
      )}
    />
  );
}

export interface MoneyInputProps extends BaseProps {
  /** Currency label shown at the left. Default: the server currency setting. */
  currency?: string;
}

/**
 * Money input: 2 decimals, thousands separators are accepted on paste but not displayed while typing.
 * Emits a 2dp number (decimal-safe through `parseMoneyInput`) or null when empty.
 */
export function MoneyInput({
  value,
  onValueChange,
  currency = getActiveCurrency(),
  min = 0,
  max,
  size = 'md',
  invalid,
  className,
  id,
  onBlur,
  onFocus,
  ...rest
}: MoneyInputProps) {
  const field = useFieldContext();
  const isInvalid = invalid ?? field?.invalid ?? false;
  const clamp = (n: number) => Math.min(max ?? Infinity, Math.max(min, n));
  const f = useNumericField(value, (n) => n.toFixed(2), parseMoneyInput, onValueChange, clamp);
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs font-medium text-fg-subtle"
      >
        {currency}
      </span>
      <input
        id={id ?? field?.id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={f.text}
        aria-invalid={isInvalid || undefined}
        aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
        onChange={(e) => {
          if (/^-?[\d,]*\.?\d{0,2}$/.test(e.target.value)) f.onChange(e.target.value);
        }}
        {...rest}
        onFocus={(e) => {
          f.onFocus();
          e.target.select();
          onFocus?.(e);
        }}
        onBlur={(e) => {
          f.onBlur();
          onBlur?.(e);
        }}
        className={cn(
          controlBase,
          controlSizes[size],
          controlBorder(isInvalid),
          'tabular pl-12 text-right',
          className,
        )}
      />
    </div>
  );
}
